import type {
  FieldValue,
  FormField,
  ImageChoiceOption,
  Option
} from '@elfsight/widget-form-builder/widget';
import { fieldValidators } from '@elfsight/widget-form-builder/widget';
import { validateTime } from '@elfsight/widget-utils';
import { format, isValid, parse } from 'date-fns';
import type { CalculatorField } from '../../types';

const DEFAULT_DATE_FORMAT = 'dd.MM.yyyy';

const STRING_TYPES = [
  'shortText',
  'longText',
  'email',
  'phone',
  'URL',
  'hidden'
];

export function findInOptions(
  value: string | number,
  options: (Option | ImageChoiceOption)[] = []
) {
  const normStrValue = value.toString().trim().toLowerCase();

  return options.find(
    ({ id, name, value: optionValue }) =>
      value === id ||
      normStrValue === name.toString().trim().toLowerCase() ||
      value === optionValue
  );
}

function isValidatorKey(
  key: string | symbol
): key is keyof typeof fieldValidators {
  return key in fieldValidators;
}

function validateStringValue(
  value: string | number | Date,
  field: FormField
): [FieldValue, boolean] | undefined {
  if (typeof value !== 'string' && typeof value !== 'number') {
    return;
  }

  const fieldType = field?.type;
  const strValue = value.toString();
  let validator;

  if (isValidatorKey(fieldType)) {
    validator = fieldValidators[fieldType];
  }

  const isValid = !validator || validator(strValue);

  return [strValue, isValid];
}

function validateNumberValue(
  value: string[] | string | number | Date,
  _field: FormField | CalculatorField
): [FieldValue, boolean] | undefined {
  if (value instanceof Date) {
    return;
  }

  const numberValue = Number(value);

  if (Array.isArray(value) || Number.isNaN(numberValue)) {
    return;
  }

  const validator = fieldValidators.number;
  const isValid = validator(numberValue);

  return [numberValue, isValid];
}

function validateTimeValue(
  value: string | number | Date,
  _field: FormField
): [FieldValue, boolean] | undefined {
  if (typeof value === 'string' && validateTime(value)) {
    return [value, true];
  }

  return;
}

function validateDateValue(
  value: string | number | Date,
  field: FormField
): [FieldValue, boolean] | undefined {
  let date;

  if (value instanceof Date) {
    date = value;
  }

  if (typeof value === 'string') {
    date = parse(value, field.dateFormat ?? DEFAULT_DATE_FORMAT, new Date());
  }

  if (date && isValid(date)) {
    const formattedDate = format(date, field.dateFormat ?? DEFAULT_DATE_FORMAT);

    return [formattedDate, true];
  }

  return;
}

function validateChoiceValue(
  value: string[] | string | number | Date,
  field: FormField | CalculatorField
): [FieldValue, boolean] | undefined {
  if (
    !('options' in field && 'multiple' in field) ||
    (!field.multiple && Array.isArray(value)) ||
    value instanceof Date
  ) {
    return;
  }

  if (Array.isArray(value)) {
    const validatedValue = value.reduce((acc: string[], valueElem) => {
      const option = findInOptions(
        valueElem,
        field.options as (Option | ImageChoiceOption)[]
      );

      if (option?.id) {
        acc.push(option.id);
      }

      return acc;
    }, []);

    return [validatedValue, true];
  }

  const option = findInOptions(
    value,
    field.options as (Option | ImageChoiceOption)[]
  );

  if (option?.id) {
    return [option.id, true];
  }

  return;
}

function validateDropdownValue(
  value: string[] | string | number | Date,
  field: FormField | CalculatorField
): [FieldValue, boolean] | undefined {
  if (
    !('options' in field) ||
    Array.isArray(value) ||
    (typeof value !== 'string' && typeof value !== 'number')
  ) {
    return;
  }

  const option = findInOptions(
    value,
    field.options as (Option | ImageChoiceOption)[]
  );

  if (option?.id) {
    return [option.id, true];
  }

  return;
}

function validateConsentValue(
  value: string | number | Date,
  _field: FormField
): [FieldValue, boolean] | undefined {
  const normValue = value.toString().toLowerCase();

  if (normValue === 'yes' || value === 1) {
    return ['Yes', true];
  }

  if (normValue === 'no' || value === 0) {
    return ['No', true];
  }

  return;
}

const submissionValidators = {
  stringType: validateStringValue,
  number: validateNumberValue,
  time: validateTimeValue,
  date: validateDateValue,
  choice: validateChoiceValue,
  imageChoice: validateChoiceValue,
  dropdown: validateDropdownValue,
  consent: validateConsentValue
};

function isSubmissionValidatorKey(
  key: string | symbol
): key is keyof typeof submissionValidators {
  return key in submissionValidators;
}

export const validateAPIValue = (
  value: string | number | Date,
  field: FormField
): [FieldValue, boolean] | undefined => {
  const fieldType = field?.type;

  if (!fieldType) {
    return;
  }

  if (STRING_TYPES.includes(fieldType)) {
    return submissionValidators.stringType(value, field);
  }

  if (!isSubmissionValidatorKey(fieldType)) {
    return;
  }

  return submissionValidators[fieldType](value, field);
};

function validateSliderValue(
  value: string[] | string | number,
  field: CalculatorField
): [FieldValue, boolean] | undefined {
  if (!('minValue' in field && 'maxValue' in field)) {
    return;
  }

  const numberValue = Number(value);

  if (
    !Number.isNaN(numberValue) &&
    numberValue >= field.minValue &&
    numberValue <= field.maxValue
  ) {
    return [numberValue, true];
  }

  return;
}

const calcValidators = {
  slider: validateSliderValue,
  number: validateNumberValue,
  dropdown: validateDropdownValue,
  choice: validateChoiceValue,
  imageChoice: validateChoiceValue,
  heading: undefined
};

export function validateCalcAPIValue(
  value: string[] | string | number,
  field: CalculatorField
): [FieldValue, boolean] | undefined {
  const fieldValidator = calcValidators[field.type];

  if (!fieldValidator) {
    return;
  }

  return fieldValidator(value, field);
}
